import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

type Option = { label: string; value: string };

interface MultiSelectProps {
  name: string;
  label?: string | null;
  placeholder?: string | null;
  options: Option[];
  control: any;
  description?: string;
  className?: string;
  selectClass?: string;
  labelClass?: string;
  required?: boolean;
  disabled?: boolean;
  notRequired?: boolean;
}

export default function MultiSelectControl({
  name,
  label,
  placeholder,
  options,
  control,
  description,
  className,
  selectClass,
  labelClass,
  required,
  disabled,
  notRequired,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedItems: string[] = field.value || [];

        const handleSelectChange = (value: string) => {
          if (selectedItems.includes(value)) {
            field.onChange(selectedItems.filter((v) => v !== value));
          } else {
            field.onChange([...selectedItems, value]);
          }
        };

        const clearSelection = () => {
          field.onChange([]);
        };

        return (
          <FormItem className={cn(className)}>
            {label && (
              <FormLabel className={labelClass}>
                {label} {required && <span className="text-red">*</span>}
              </FormLabel>
            )}
            <FormControl>
              <div className="relative" ref={containerRef}>
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center justify-between gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring border border-input shadow-sm h-9 px-4 py-2 w-full rounded-lg',
                    selectClass,
                    disabled && 'opacity-50 cursor-not-allowed',
                    'bg-white',
                  )}
                  disabled={disabled}
                  onClick={() => setOpen((prev) => !prev)}
                >
                  <span className="truncate text-left">
                    {selectedItems.length > 0
                      ? options
                          .filter((opt) => selectedItems.includes(opt.value))
                          .map((opt) => opt.label)
                          .join(', ')
                      : placeholder || ''}
                  </span>
                  {selectedItems.length <= 0 && (
                    <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
                  )}
                </button>

                {open && options.length > 0 && (
                  <div className="absolute z-[100] mt-1 w-full min-w-[200px] rounded-md border bg-white shadow-lg">
                    <div className="max-h-[300px] overflow-y-auto p-1">
                      {options.map((option) => {
                        const isSelected = selectedItems.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={cn(
                              'flex w-full items-center text-left gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-gray-100',
                              isSelected && 'bg-gray-50',
                            )}
                            onClick={() => handleSelectChange(option.value)}
                          >
                            <div
                              className={cn(
                                'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                                isSelected
                                  ? 'bg-primary border-primary text-white'
                                  : 'border-gray-300',
                              )}
                            >
                              {isSelected && <Check className="h-3 w-3" />}
                            </div>
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedItems.length > 0 && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 z-10"
                    title="Clear selection"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            {!notRequired && (
              <div className="h-4 !mt-1">
                <FormMessage />
              </div>
            )}
          </FormItem>
        );
      }}
    />
  );
}
